import gsap from 'gsap';
import isTailwindBreakpoint from '../utils/isTailwindBreakpoint';
import { whenReady as whenSwatchesReady } from '../utils/product-swatches-registry';

export default class ProductCard extends HTMLElement {
  private isEmpty: boolean = false;
  private forceMobileLayout: boolean = false;

  private link: HTMLElement | null = null;
  private medias: HTMLElement | null = null;
  private mediaSecondary: HTMLElement | null = null;
  private infosPrimary: HTMLElement | null = null;
  private infosSecondary: HTMLElement | null = null;
  private mediasScrollbar: HTMLElement | null = null;
  private swatchesList: HTMLElement | null = null;
  private smallSwatchTemplate: HTMLTemplateElement | null = null;

  private mouseenterTl: GSAPTimeline | null = null;
  private mouseleaveTl: GSAPTimeline | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onUpdateBound = this.onUpdate.bind(this);
  private onMouseEnterBound = this.onMouseEnter.bind(this);
  private onMouseLeaveBound = this.onMouseLeave.bind(this);
  private onFocusBound = this.onFocus.bind(this);
  private onBlurBound = this.onBlur.bind(this);
  private onMediaScrollBound = this.onMediaScroll.bind(this);

  private mediasWidth: number = 0;
  private mediasScrollBarWidth: number = 0;
  private mediasScrollWidth: number = 0;
  private mediasScrollLeft: number = 0;

  private rafId: number | null = null;

  private isMobile: boolean = isTailwindBreakpoint('lg');

  connectedCallback(): void {
    this.isEmpty = this.dataset.isEmpty === 'true';
    this.forceMobileLayout = this.dataset.forceMobileLayout === 'true';

    if (this.isEmpty) {
      return;
    }

    if (this.forceMobileLayout) {
      this.isMobile = false;
    }

    this.link = this.querySelector('[data-product-card-ref="link"]');
    this.medias = this.querySelector('[data-product-card-ref="medias"]');
    this.mediaSecondary = this.querySelector('[data-product-card-ref="media-secondary"]');
    this.infosPrimary = this.querySelector('[data-product-card-ref="infos-primary"]');
    this.infosSecondary = this.querySelector('[data-product-card-ref="infos-secondary"]');
    this.mediasScrollbar = this.querySelector('[data-product-card-ref="medias-scrollbar"]');
    this.swatchesList = this.querySelector('[data-product-card-ref="swatches"]');
    this.smallSwatchTemplate = this.querySelector('[data-product-card-ref="small-swatch-template"]');

    this.addEventListeners();
    this.syncSiblingSwatches();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    this.onResize();

    if (this.link && !this.forceMobileLayout) {
      this.link.addEventListener('mouseenter', this.onMouseEnterBound);
      this.link.addEventListener('mouseleave', this.onMouseLeaveBound);
      this.link.addEventListener('focus', this.onFocusBound);
      this.link.addEventListener('blur', this.onBlurBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);
    if (this.link) {
      this.link.removeEventListener('mouseenter', this.onMouseEnterBound);
      this.link.removeEventListener('mouseleave', this.onMouseLeaveBound);
      this.link.removeEventListener('focus', this.onFocusBound);
      this.link.removeEventListener('blur', this.onBlurBound);
    }

    if (this.medias) {
      this.medias.removeEventListener('scroll', this.onMediaScrollBound);
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }

  onResize(): void {
    if (this.medias && this.mediasScrollbar) {
      this.mediasWidth = this.medias.getBoundingClientRect().width;
      this.mediasScrollWidth = this.medias.scrollWidth;
      this.mediasScrollBarWidth = this.mediasScrollbar.getBoundingClientRect().width;
    }

    const isMobile = this.forceMobileLayout || !isTailwindBreakpoint('lg');

    if (this.isMobile !== isMobile) {
      if (isMobile) {
        gsap.set(this.mediaSecondary, { autoAlpha: 1 });
        gsap.set(this.infosPrimary, { autoAlpha: 1 });
        gsap.set(this.infosSecondary, { autoAlpha: 1 });

        if (this.medias) {
          this.medias.addEventListener('scroll', this.onMediaScrollBound);
        }

        this.onUpdateBound();
      } else {
        if (this.medias) {
          this.medias.scrollLeft = 0;
        }
        gsap.set(this.mediaSecondary, { autoAlpha: 0 });
        gsap.set(this.infosPrimary, { autoAlpha: 1 });
        gsap.set(this.infosSecondary, { autoAlpha: 0 });

        if (this.medias) {
          this.medias.removeEventListener('scroll', this.onMediaScrollBound);
        }

        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }
      }
    }
    this.isMobile = isMobile;
  }

  onUpdate() {
    this.rafId = requestAnimationFrame(this.onUpdateBound);

    if (this.medias && this.medias.scrollWidth > 0 && this.mediasScrollbar) {
      const maxScrollLeft = this.mediasScrollWidth - this.mediasWidth;
      const maxTranslateX = this.mediasWidth - this.mediasScrollBarWidth;

      if (maxScrollLeft > 0 && maxTranslateX > 0) {
        const progress = this.mediasScrollLeft / maxScrollLeft;
        const clampedProgress = Math.min(Math.max(progress, 0), 1);
        const translateX = (clampedProgress * maxTranslateX).toFixed(2);
        this.mediasScrollbar.style.transform = `translate3d(${translateX}px, 0, 0)`;
      } else {
        this.mediasScrollbar.style.transform = '';
      }
    }
  }

  private async syncSiblingSwatches(): Promise<void> {
    if (!this.swatchesList || !this.smallSwatchTemplate) return;

    const productId = this.dataset.productId;
    if (!productId) return;

    const { products } = await whenSwatchesReady();
    const entry = products[productId];
    if (!entry) return;

    const renderedHandles = new Set(
      Array.from(this.swatchesList.querySelectorAll('small-swatch'))
        .map((swatch) => (swatch as HTMLElement).dataset.colorHandle)
        .filter((handle): handle is string => Boolean(handle)),
    );

    const siblingHandles = new Set(
      entry.siblingIds
        .map((siblingId) => products[siblingId]?.color)
        .filter((handle): handle is string => Boolean(handle)),
    );

    for (const handle of siblingHandles) {
      if (renderedHandles.has(handle)) continue;

      const fragment = this.smallSwatchTemplate.content.cloneNode(true) as DocumentFragment;
      const swatch = fragment.querySelector('small-swatch') as HTMLElement | null;

      if (!swatch) continue;

      swatch.dataset.colorHandle = handle;
      this.swatchesList.appendChild(fragment);
    }
  }

  onMediaScroll(): void {
    if (this.medias) {
      this.mediasScrollLeft = this.medias.scrollLeft;
    }
  }

  onMouseEnter(): void {
    if (this.forceMobileLayout) {
      return;
    }

    if (this.mouseleaveTl) {
      this.mouseleaveTl.kill();
    }

    if (isTailwindBreakpoint('lg')) {
      this.mouseenterTl = gsap.timeline();
      this.mouseenterTl.to(this.mediaSecondary, { autoAlpha: 1, duration: 0.15, ease: 'power3.out' }, 0);
      this.mouseenterTl.to(this.infosPrimary, { autoAlpha: 0, duration: 0, ease: 'linear' }, 0);
      this.mouseenterTl.to(this.infosSecondary, { autoAlpha: 1, duration: 0, ease: 'linear' }, 0);
    }
  }

  onMouseLeave(): void {
    if (this.forceMobileLayout) {
      return;
    }

    if (this.mouseenterTl) {
      this.mouseenterTl.kill();
    }

    if (isTailwindBreakpoint('lg')) {
      this.mouseleaveTl = gsap.timeline();
      this.mouseleaveTl.to(this.mediaSecondary, { autoAlpha: 0, duration: 0.15, ease: 'power3.out' }, 0);
      this.mouseleaveTl.to(this.infosPrimary, { autoAlpha: 1, duration: 0, ease: 'linear' }, 0);
      this.mouseleaveTl.to(this.infosSecondary, { autoAlpha: 0, duration: 0, ease: 'linear' }, 0);
    }
  }

  onFocus(): void {
    this.onMouseEnter();
  }

  onBlur(): void {
    this.onMouseLeave();
  }

  disconnectedCallback(): void {
    if (this.mouseenterTl) {
      this.mouseenterTl.kill();
    }
    if (this.mouseleaveTl) {
      this.mouseleaveTl.kill();
    }

    this.removeEventListeners();
  }
}

customElements.define('product-card', ProductCard);
